import { db } from '../config/firebase';
import { collection, doc, getDoc, getDocs, query, where, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { Organization, PLAN_DEFINITIONS } from '../types/organization';

/**
 * Get organization by slug (URL-friendly identifier)
 */
export const getOrganizationBySlug = async (slug: string): Promise<Organization | null> => {
  try {
    const q = query(collection(db, 'organizations'), where('slug', '==', slug));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    
    return {
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate(),
      subscription: {
        ...data.subscription,
        currentPeriodEnd: data.subscription?.currentPeriodEnd?.toDate(),
        trialEnds: data.subscription?.trialEnds?.toDate(),
      },
    } as Organization;
  } catch (error) {
    console.error('Error fetching organization by slug:', error);
    return null;
  }
};

/**
 * Get organization by custom domain
 */
export const getOrganizationByCustomDomain = async (domain: string): Promise<Organization | null> => {
  try {
    const q = query(
      collection(db, 'organizations'),
      where('branding.customDomain', '==', domain)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    
    return {
      ...data,
      id: doc.id,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate(),
      subscription: {
        ...data.subscription,
        currentPeriodEnd: data.subscription?.currentPeriodEnd?.toDate(),
        trialEnds: data.subscription?.trialEnds?.toDate(),
      },
    } as Organization;
  } catch (error) {
    console.error('Error fetching organization by custom domain:', error);
    return null;
  }
};

/**
 * Get organization by ID
 */
export const getOrganizationById = async (orgId: string): Promise<Organization | null> => {
  try {
    const docRef = doc(db, 'organizations', orgId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const data = docSnap.data();
    
    return {
      ...data,
      id: docSnap.id,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate(),
      subscription: {
        ...data.subscription,
        currentPeriodEnd: data.subscription?.currentPeriodEnd?.toDate(),
        trialEnds: data.subscription?.trialEnds?.toDate(),
      },
    } as Organization;
  } catch (error) {
    console.error('Error fetching organization by ID:', error);
    return null;
  }
};

/**
 * Create a new organization
 */
export const createOrganization = async (
  orgData: Omit<Organization, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Organization> => {
  try {
    const orgRef = doc(collection(db, 'organizations'));
    
    // Set default features based on plan
    const plan = orgData.subscription.plan;
    const planFeatures = PLAN_DEFINITIONS[plan].features;
    
    const now = new Date();
    
    const newOrg: Omit<Organization, 'id'> = {
      ...orgData,
      features: {
        playerRankings: planFeatures.playerRankings || false,
        statistics: planFeatures.statistics || false,
        tournaments: planFeatures.tournaments || false,
        customBranding: planFeatures.customBranding || false,
        apiAccess: planFeatures.apiAccess || false,
        whiteLabel: planFeatures.whiteLabel || false,
        advancedAnalytics: planFeatures.advancedAnalytics || false,
      },
      subscription: {
        ...orgData.subscription,
        maxPlayers: PLAN_DEFINITIONS[plan].maxPlayers,
        maxCompetitions: PLAN_DEFINITIONS[plan].maxCompetitions,
      },
      createdAt: now,
      updatedAt: now,
    };
    
    await setDoc(orgRef, {
      ...newOrg,
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
    });
    
    return {
      ...newOrg,
      id: orgRef.id,
    };
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error;
  }
};

/**
 * Update organization
 */
export const updateOrganization = async (
  orgId: string,
  updates: Partial<Organization>
): Promise<void> => {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    
    await updateDoc(orgRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating organization:', error);
    throw error;
  }
};

/**
 * Update organization branding
 */
export const updateOrganizationBranding = async (
  orgId: string,
  branding: Partial<Organization['branding']>
): Promise<void> => {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    
    await updateDoc(orgRef, {
      branding,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating organization branding:', error);
    throw error;
  }
};

/**
 * Check if slug is available
 */
export const isSlugAvailable = async (slug: string): Promise<boolean> => {
  try {
    const org = await getOrganizationBySlug(slug);
    return org === null;
  } catch (error) {
    console.error('Error checking slug availability:', error);
    return false;
  }
};

/**
 * Get all organizations for a user (admin access)
 */
export const getUserOrganizations = async (userId: string): Promise<Organization[]> => {
  try {
    const q = query(
      collection(db, 'organizations'),
      where(`admins.${userId}`, '==', true)
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate(),
        subscription: {
          ...data.subscription,
          currentPeriodEnd: data.subscription?.currentPeriodEnd?.toDate(),
          trialEnds: data.subscription?.trialEnds?.toDate(),
        },
      } as Organization;
    });
  } catch (error) {
    console.error('Error fetching user organizations:', error);
    return [];
  }
};
